import { array, option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { identity } from 'fp-ts/lib/function';

import type { ParticularKey } from '../types/ParticularKey';
import type { Spreadable } from '../types/Spreadable';

interface PropFn {
  <T, K extends keyof T>(key: K): (obj: T) => T[K];
  <T, K1 extends keyof T, K2 extends keyof T[K1]>(
    k1: K1,
    k2: K2,
  ): (obj: T) => T[K1][K2];
  <T, K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2]>(
    k1: K1,
    k2: K2,
    k3: K3,
  ): (obj: T) => T[K1][K2][K3];
  <
    T,
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
  >(
    k1: K1,
    k2: K2,
    k3: K3,
    k4: K4,
  ): (obj: T) => T[K1][K2][K3][K4];
  <
    T,
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4],
  >(
    k1: K1,
    k2: K2,
    k3: K3,
    k4: K4,
    k5: K5,
  ): (obj: T) => T[K1][K2][K3][K4][K5];
}

/**
 * Get value from object under given property path
 *
 * All attributes on the path must be mandatory. If you need to access a value
 * nested in optional attributes, use maybeProp.
 *
 * @example
 * pipe(
 *   {foo: {bar: 'baz'}},
 *   prop('foo', 'bar') // equivalent to x => x.foo.bar
 * ); // <- 'baz'
 */
export const prop: PropFn =
  (...keys: (keyof any)[]) =>
  (obj: any) =>
    keys.reduce((acc: any, key) => acc[key], obj);

type NN1<T, K1 extends keyof T> = NonNullable<T[K1]>;
type NN2<T, K1 extends keyof T, K2 extends keyof NN1<T, K1>> = NonNullable<
  NN1<T, K1>[K2]
>;
type NN3<
  T,
  K1 extends keyof T,
  K2 extends keyof NN1<T, K1>,
  K3 extends keyof NN2<T, K1, K2>,
> = NonNullable<NN2<T, K1, K2>[K3]>;

/**
 * keyof never = string | number | symbol, but we want to restrict
 * non-existent keys arguments in MaybeProp. A problem may occur
 * with maybeProp<{x: null | undefined}, 'x', 'foo'> - this would
 * falsely pass when using plain keyof, because:
 * keyof NotNullable<{x: null | undefined}> = keyof never = everything is possible.
 */
type KeyOfNotNever<T> = [T] extends [never] ? never : keyof T;
type DefinedOption<T> = Option<Exclude<T, undefined>>;

interface MaybePropFn {
  <T, K extends KeyOfNotNever<T>>(
    key: K,
  ): (obj?: T | null) => DefinedOption<T[K]>;
  <T, K1 extends KeyOfNotNever<T>, K2 extends KeyOfNotNever<NN1<T, K1>>>(
    k1: K1,
    k2: K2,
  ): (obj?: T | null) => DefinedOption<NN1<T, K1>[K2]>;
  <
    T,
    K1 extends KeyOfNotNever<T>,
    K2 extends KeyOfNotNever<NN1<T, K1>>,
    K3 extends KeyOfNotNever<NN2<T, K1, K2>>,
  >(
    k1: K1,
    k2: K2,
    k3: K3,
  ): (obj?: T | null) => DefinedOption<NN2<T, K1, K2>[K3]>;
  <
    T,
    K1 extends KeyOfNotNever<T>,
    K2 extends KeyOfNotNever<NN1<T, K1>>,
    K3 extends KeyOfNotNever<NN2<T, K1, K2>>,
    K4 extends KeyOfNotNever<NN3<T, K1, K2, K3>>,
  >(
    k1: K1,
    k2: K2,
    k3: K3,
    k4: K4,
  ): (obj?: T | null) => DefinedOption<NN3<T, K1, K2, K3>[K4]>;
}

const makeMaybeProp = (toSome: (x: any) => any, none: unknown): any => {
  const maybePropImpl =
    (k: keyof any, ...tail: (keyof any)[]) =>
    (obj: any) => {
      // Outer typings are defined by MaybePropFn / MaybePropNFn.
      // Inner typing of this function would be too complicated to be worth the result.
      const next = obj?.[k];
      if (next === undefined) {
        return none;
      }

      return array.isNonEmpty(tail)
        ? (maybePropImpl as any)(...tail)(next)
        : toSome(next);
    };
  return maybePropImpl;
};

/**
 * Get value from object under given optional property path or option.none on non-existent or undefined
 *
 * This function will return option.none in these cases:
 * - not all keys exist on the object path
 * - value under one of the keys is null or undefined
 * - the value exists, but is undefined
 *
 * The later case may be counter-intuitive in some cases because the value exists,
 * but it makes more sense for two reasons:
 * - semantics of undefined is a "missing value"
 * - {a: undefined} is assignable to {a?: number} and it will be neither expected
 *   nor type-correct to return some(undefined) from prop('a') since the inferred return
 *   type will be number.
 */
export const maybeProp: MaybePropFn = makeMaybeProp(option.some, option.none);

type DefinedNullable<T> = Exclude<T, undefined> | null;

interface MaybePropNFn {
  <T, K extends KeyOfNotNever<T>>(
    key: K,
  ): (obj?: T | null) => DefinedNullable<T[K]>;
  <T, K1 extends KeyOfNotNever<T>, K2 extends KeyOfNotNever<NN1<T, K1>>>(
    k1: K1,
    k2: K2,
  ): (obj?: T | null) => DefinedNullable<NN1<T, K1>[K2]>;
  <
    T,
    K1 extends KeyOfNotNever<T>,
    K2 extends KeyOfNotNever<NN1<T, K1>>,
    K3 extends KeyOfNotNever<NN2<T, K1, K2>>,
  >(
    k1: K1,
    k2: K2,
    k3: K3,
  ): (obj?: T | null) => DefinedNullable<NN2<T, K1, K2>[K3]>;
  <
    T,
    K1 extends KeyOfNotNever<T>,
    K2 extends KeyOfNotNever<NN1<T, K1>>,
    K3 extends KeyOfNotNever<NN2<T, K1, K2>>,
    K4 extends KeyOfNotNever<NN3<T, K1, K2, K3>>,
  >(
    k1: K1,
    k2: K2,
    k3: K3,
    k4: K4,
  ): (obj?: T | null) => DefinedNullable<NN3<T, K1, K2, K3>[K4]>;
}

/**
 * Nullable variant of maybeProp
 *
 * Returns value | null instead of Option<value>
 *
 * @see maybeProp
 */
export const maybePropN: MaybePropNFn = makeMaybeProp(identity, null);

export const propOf =
  <T>(obj: T) =>
  <K extends keyof T>(key: K): T[K] =>
    obj[key];
export const maybePropOf =
  <T>(obj?: T | null) =>
  <K extends keyof T>(key: K): Option<NonNullable<T[K]>> =>
    option.fromNullable(obj?.[key]);
export const maybePropOfN =
  <T>(obj?: T | null) =>
  <K extends keyof T>(key: K): NonNullable<T[K]> | null =>
    obj?.[key] ?? null;

export const removeProp =
  <T, K extends keyof T>(...keys: K[]) =>
  (obj: T): Omit<T, K> => {
    const result = { ...obj };
    for (const key of keys) {
      delete result[key];
    }
    return result;
  };

/**
 * Add new prop to object
 *
 * Technically is the same as `setProp` below, but typings are configured
 * to extend original object's signature with new key. However the key must be specified or inferred
 * to a particular one by Typescript. If the key is not exactly known at compile-time, there is
 * no way to safely extend the type signature. Use `setProp` in such cases.
 */
export const addProp =
  <K extends keyof any, V>(key: ParticularKey<K>, value: V) =>
  <T extends Spreadable>(obj: T): T & { [Key in K]: V } =>
    ({
      ...obj,
      [key]: value,
    }) as T & { [Key in K]: V }; // type assertion needed since typescript cannot determine key is still K

/**
 * @see addProp - this variant just differs in partial application order
 */
export const addPropTo =
  <T extends Spreadable, K extends keyof any>(obj: T, key: ParticularKey<K>) =>
  <V>(value: V): T & { [Key in K]: V } =>
    addProp(key, value)(obj);

/**
 * Set a prop on an object (immutably)
 *
 * Technically is the same as `addProp` above, but typings are configured
 * to expect the given property on the original object and not change
 * the type signature.
 */
export const setProp: <T, K extends keyof T>(
  key: K,
  value: T[K],
) => (obj: T) => T = addProp as any;

/**
 * @see setProp - this variant just differs in partial application order
 */
export const setPropTo: <T, K extends keyof T>(
  obj: T,
  key: K,
) => (value: T[K]) => T = addPropTo as any;

export const setPropMutable =
  <T, K extends keyof T = keyof T>(key: K, value: T[K]) =>
  (obj: T): T => {
    obj[key] = value;
    return obj;
  };

/**
 * Add a new property to a mutable object
 *
 * Since this function returns the original object, it is a nice way to extend types without assertions.
 *
 * Following example does not work when Foo has no "extra" attribute and we really mean to extend the type.
 * And it is difficult to impossible to workaround it without type assertions and therefore type safety compromises.
 * ```
 * const fooExtra = new Foo(); // better to avoid "as Foo & {extra: string}" - the extra attribute is not there yet,
 *                             // and we risk that we forget to update it on refactoring and end up with a complete lie
 *                             // like `new Bar() as Foo & {extra: string}`.
 * fooExtra.extra = 'hi there'; // error
 * (fooExtra as any).extra = 'hi there'; // we can bypass it like this
 * console.log(fooExtra.extra);
 * fooExtra.extra = 'hi there'; // error again
 * ```
 *
 * But now, now we can do this. No assertions, no lies, almost perfectly type safe.
 * ```
 * const fooExtra: Foo & {extra: string} = pipe(
 *   new Foo(),
 *   addPropMutable('extra', 'hi there'),
 * );
 * ```
 */
export const addPropMutable: <K extends keyof any, V>(
  key: ParticularKey<K>,
  value: V,
) => <T>(obj: T) => T & { [Key in K]: V } = setPropMutable as any;

export const testable = {
  makeMaybeProp,
};
